export type OrgEnvironment = "PRODUCTION" | "SANDBOX";

export type ACCOUNT_MATCHING_PREFERRED_COUNTRY_FIELD = string;

export interface SFDCIntegration {
  id: string;
  name: string;
  environment: OrgEnvironment;
  organizationId: string;
  managedPackageCurrentVersion?: string;
  needsResync?: boolean;
  lastSyncFailed: boolean;
  syncSettings: SFDCIntegrationSyncSettings;
}

export interface SFDCIntegrationSyncSettings {
  autoSync: boolean;
  syncAccounts: boolean;
  syncLeads: boolean;
  exportTechInstalls: boolean;
  exportItSpend: boolean;
  exportFirmographics: boolean;
  exportAccountScoring: boolean;
  exportCloudDynamics: boolean;
  selectedProducts: number[];
  selectedSpendCategories: string[];
  selectedScoringId?: number;
  useCountryLevelMatching: boolean;
  accountsCountryField: ACCOUNT_MATCHING_PREFERRED_COUNTRY_FIELD;
  localizeInstalls: boolean;
  customHgIdMatching: boolean;
}

export interface SFDCIntegrationSyncResults {
  totalRecords?: number;
  matchedRecords?: number;
  notMatchedRecords?: number;
  syncTime?: Date;
  hasErrors?: boolean;
}

export enum EnrichmentSynchronizationState {
  AVAILABLE = "AVAILABLE",
  NEVER_RAN_MATCHING = "NEVER_RAN_MATCHING",
  CHOOSE_SPEND_OR_PRODUCT = "CHOOSE_SPEND_OR_PRODUCT",
  SYNC_IN_PROGRESS = "UNAVAILABLE",
  INTEGRATION_UNHEALTHY = "INTEGRATION_UNHEALTHY",
  RECORDS_CAP_EXCEEDED = "RECORDS_CAP_EXCEEDED",
  CREDITS_EXHAUSTED = "CREDITS_EXHAUSTED",
}

export interface IntegrationSynchronization {
  state: EnrichmentSynchronizationState;
}

// GraphQL Query/Mutation Variables
export type SfdcIntegrationsVariables = Record<string, never>;

export interface GetLatestSyncResultVariables {
  integrationId?: string;
}

export interface GetIntegrationSynchronizationVariables {
  integrationId?: string;
}

export interface CreateIntegrationVariables {
  authorizationCodeGrant: string;
  redirectUri: string;
  name: string;
  createdByEmail: string;
  environment: string;
  autoSync: boolean;
  syncAccounts: boolean;
  syncLeads: boolean;
  exportTechInstalls: boolean;
  exportItSpend: boolean;
  exportFirmographics: boolean;
  exportAccountScoring: boolean;
  exportCloudDynamics: boolean;
  useCountryLevelMatching: boolean;
  accountsCountryField?: string;
  localizeInstalls: boolean;
  customHgIdMatching: boolean;
}

export interface ReauthenticateIntegrationVariables {
  authorizationCodeGrant: string;
  redirectUri: string;
  integrationId: string;
}

export interface IntegrationSyncSettingsInput {
  autoSync: boolean;
  syncAccounts: boolean;
  syncLeads: boolean;
  exportTechInstalls: boolean;
  exportItSpend: boolean;
  exportFirmographics: boolean;
  exportAccountScoring: boolean;
  exportCloudDynamics: boolean;
  useCountryLevelMatching: boolean;
  accountsCountryField?: string;
  localizeInstalls: boolean;
  customHgIdMatching: boolean;
}

export interface UpdateIntegrationSyncSettingsVariables {
  integrationId: string;
  syncSettings: IntegrationSyncSettingsInput;
}

// GraphQL Response Types
export interface SfdcIntegrationsResponse {
  integrations: SFDCIntegration[];
}

export interface GetLatestSyncResultResponse {
  latestSyncResult: SFDCIntegrationSyncResults;
}

export interface GetIntegrationSynchronizationResponse {
  enrichmentSynchronization: IntegrationSynchronization;
}

export interface CreateIntegrationResponse {
  createIntegration: {
    result: Pick<
      SFDCIntegration,
      "id" | "name" | "environment" | "organizationId"
    >;
  };
}

export interface ReauthenticateIntegrationResponse {
  reauthenticateIntegration: {
    result: Pick<
      SFDCIntegration,
      "id" | "name" | "environment" | "organizationId"
    >;
  };
}

export interface UpdateIntegrationSyncSettingsResponse {
  updateIntegrationSyncSettings: Pick<SFDCIntegration, "id" | "syncSettings">;
}
